import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { ShiftRateNegotiationStatus } from "@src/appV2/OpenShifts/RateNegotiation/types";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { usePendingPostBookingRequirementsForShifts } from "@src/appV2/redesign/Documents/api/wrappers/usePendingPostBookingRequirementsForShifts";
import { buildWorkplaceDocumentsPath } from "@src/appV2/redesign/Documents/paths";
import { getOrientationPayDelta } from "@src/appV2/redesign/Shift/PreShiftOrientation/getOrientationPayDelta";
import { type ModalShiftData } from "@src/appV2/redesign/Shift/types";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";

import {
  SHIFT_DISCOVERY_CHILDCARE_REFERRAL_PROMPT_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_OPEN_SHIFTS_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useWorkplaceModalsDataContext } from "../../ShiftDiscovery/useWorkplaceModalsDataContext";
import { getShiftPayRates } from "../../utils/calculateShiftPay";
import { useWorkplaceOpenShiftsData } from "../../Workplace/OpenShifts/useWorkplaceOpenShiftsData";
import { useChildcareReferralPostBookingIam } from "./ChildcareReferralPrompt/useChildcareReferralPostBookingIam";
import { RegularShiftBookedBottomSheet } from "./RegularShiftBookedBottomSheet";

interface RegularShiftBookedBottomSheetContainerProps {
  modalState: UseModalState;
  shift: ModalShiftData;
}

export function RegularShiftBookedBottomSheetContainer(
  props: RegularShiftBookedBottomSheetContainerProps
) {
  const { modalState, shift } = props;

  const {
    workplace,
    breakPolicy,
    attributes: { timeSlot, start, end, qualification, isUrgent },
  } = shift;

  const history = useHistory();
  const { generateModalPath } = useWorkplaceModalsDataContext();
  const {
    availableShiftBoost,
    appliedShiftBoost,
    hasPreShiftOrientation,
    preShiftOrientationAdjustedStart,
    preShiftOrientationLengthInMinutes,
    navigateToModal,
  } = useShiftModalsDataContext();

  const { isEligible: isChildcareReferralIamEligible, markAsShown: markChildcareReferralIamShown } =
    useChildcareReferralPostBookingIam(shift);

  const childcareReferralPromptModalState = useMemo<UseModalState>(() => {
    if (!isChildcareReferralIamEligible) {
      return modalState;
    }

    return {
      ...modalState,
      closeModal: () => {
        markChildcareReferralIamShown();
        navigateToModal(SHIFT_DISCOVERY_CHILDCARE_REFERRAL_PROMPT_MODAL_PATH, {
          pathParams: { shiftId: shift.id },
          forceReplacePath: true,
        });
      },
    };
  }, [
    isChildcareReferralIamEligible,
    markChildcareReferralIamShown,
    modalState,
    navigateToModal,
    shift.id,
  ]);

  const rateNegotiation =
    shift.rateNegotiation?.status === ShiftRateNegotiationStatus.ACCEPTED
      ? shift.rateNegotiation
      : undefined;

  const { totalPay, totalPayFormatted, hourlyPayFormatted, hourlyPay } = getShiftPayRates({
    shift,
    rateNegotiationId: rateNegotiation?.id,
  });

  const orientationApplies = hasPreShiftOrientation && isDefined(preShiftOrientationAdjustedStart);

  const effectiveShiftStartIso = orientationApplies
    ? preShiftOrientationAdjustedStart.toISOString()
    : start;
  const orientationPayDelta = getOrientationPayDelta({
    hourlyPay,
    hasPreShiftOrientation,
    orientationLengthInMinutes: preShiftOrientationLengthInMinutes,
  });
  const effectiveTotalPay = totalPay + orientationPayDelta;
  const effectiveTotalPayFormatted = orientationApplies
    ? formatDollarsAsUsd(effectiveTotalPay)
    : totalPayFormatted;

  const {
    data: { unfilteredWorkplaceShifts },
    isLoading: openShiftsIsLoading,
  } = useWorkplaceOpenShiftsData(workplace);

  const hasOpenShifts = unfilteredWorkplaceShifts.length > 0;
  const shouldShowOpenShifts = hasOpenShifts && !isUrgent;

  const { data: qualificationDisplayLabels, isLoading: isLoadingQualification } =
    useGetQualificationDisplayLabels([qualification]);
  // eslint-disable-next-line prefer-destructuring
  const qualificationText = qualificationDisplayLabels[0];

  const shiftInputs = useMemo(
    () => [
      {
        shiftId: shift.id,
        workplaceId: workplace.id,
        workplaceType: workplace.attributes.type,
        msa: workplace.attributes.location.msa,
        state: workplace.attributes.location.state,
        region: workplace.attributes.location.region,
        qualification,
      },
    ],
    [shift.id, workplace, qualification]
  );

  const { resultsByShiftId, isLoading: isLoadingPendingPostBookingRequirements } =
    usePendingPostBookingRequirementsForShifts(shiftInputs);
  const pendingPostBookingRequirementsCount = resultsByShiftId[shift.id]?.count;

  useLogEffect(
    APP_V2_USER_EVENTS.POST_BOOKING_DOCUMENTS_SHEET_BANNER_SHOWN,
    {
      shiftId: shift.id,
      workplaceId: workplace.id,
      qualification,
      count: pendingPostBookingRequirementsCount,
    },
    {
      enabled:
        modalState.modalIsOpen &&
        isDefined(pendingPostBookingRequirementsCount) &&
        pendingPostBookingRequirementsCount > 0,
    }
  );

  const onCompleteDocumentsClick = () => {
    logEvent(APP_V2_USER_EVENTS.POST_BOOKING_DOCUMENTS_SHEET_CTA_CLICKED, {
      shiftId: shift.id,
      workplaceId: workplace.id,
      qualification,
      count: pendingPostBookingRequirementsCount,
    });

    modalState.closeModal();
    history.push(
      buildWorkplaceDocumentsPath({
        workplaceId: workplace.id,
        qualification,
        shiftId: shift.id,
        postBookingRequirementsFilter: "include-only",
      })
    );
  };

  return (
    <RegularShiftBookedBottomSheet
      modalState={childcareReferralPromptModalState}
      shiftStartIso={effectiveShiftStartIso}
      shiftEndIso={end}
      shiftTimeSlot={timeSlot}
      workplaceName={workplace.attributes.name}
      workplaceTimezone={workplace.attributes.location.timezone}
      shiftHourlyPay={hourlyPayFormatted}
      shiftFormattedTotalPay={effectiveTotalPayFormatted}
      shiftTotalPay={effectiveTotalPay}
      shiftBoost={appliedShiftBoost?.attributes.amount ?? availableShiftBoost?.attributes.amount}
      requiresBreak={(workplace.attributes.requiresLunchBreak ?? false) && isDefined(breakPolicy)}
      breakDurationInMinutes={breakPolicy?.breakDurationInMinutes ?? 0}
      isLoadingQualification={isLoadingQualification}
      qualificationText={qualificationText}
      workplaceActionLabel={
        shouldShowOpenShifts
          ? `See ${unfilteredWorkplaceShifts.length} workplace shifts`
          : "See workplace profile"
      }
      isWorkplaceActionLoading={openShiftsIsLoading}
      pendingPostBookingRequirementsCount={pendingPostBookingRequirementsCount}
      isLoadingPendingPostBookingRequirements={isLoadingPendingPostBookingRequirements}
      shiftId={shift.id}
      workplaceId={workplace.id}
      workplaceType={workplace.attributes.type}
      msa={workplace.attributes.location.msa}
      onCompleteDocumentsClick={onCompleteDocumentsClick}
      onWorkplaceActionClick={() => {
        logEvent(
          shouldShowOpenShifts
            ? APP_V2_USER_EVENTS.WORKPLACE_OPEN_SHIFTS_OPENED
            : APP_V2_USER_EVENTS.WORKPLACE_PROFILE_OPENED,
          {
            workplaceId: workplace?.id,
            trigger: "Shift Booked Bottom Sheet",
          }
        );
        const modalPath = shouldShowOpenShifts
          ? generateModalPath(SHIFT_DISCOVERY_WORKPLACE_OPEN_SHIFTS_MODAL_PATH, {
              workplaceId: workplace?.id,
            })
          : generateModalPath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH, {
              workplaceId: workplace?.id,
            });
        modalState.closeModal();
        history.push(modalPath);
      }}
    />
  );
}
